/**
 * «Por revisar» (al estilo de YNAB): movimientos que llegan del banco o de un
 * archivo con `reviewed = false` hasta que se aprueban. Lógica pura:
 * agrupar por fecha, selección, totales, sugerencia de categoría y el
 * siguiente pendiente.
 */

export type MovimientoPendiente = {
  id: string;
  date: string;
  amount: number;
  payee: string | null;
  category: string | null;
  account: string;
  is_parent?: boolean;
  transfer_id?: string | null;
  /** Partes de un dividido (solo lo necesario para saber si se aprueba). */
  partes?: readonly ParteDividida[];
};

export type ParteDividida = {
  category: string | null;
  transfer_id?: string | null;
};

/**
 * Pendiente «sin categoría» de verdad: ni dividido (la categoría va en las
 * partes), ni traspaso entre cuentas, ni de una cuenta fuera del presupuesto
 * (esos no llevan categoría).
 */
export function esSinCategoria(
  m: MovimientoPendiente,
  cuentasFuera: ReadonlySet<string>,
): boolean {
  return (
    !m.category &&
    !m.is_parent &&
    !m.transfer_id &&
    !cuentasFuera.has(m.account)
  );
}

/**
 * Un traspaso entre dos cuentas del presupuesto no lleva categoría (no es
 * gasto ni ingreso); uno que sale hacia una cuenta fuera del presupuesto sí
 * puede llevarla, la de la que sale el dinero (Vadik, 27-oct-2026: «si va a
 * una cuenta de ahorro y tienes la categoría Colchón…»). `destino` es la
 * cuenta del otro lado (la del comercio «traspaso»).
 */
export function tipoTraspaso(
  m: MovimientoPendiente,
  cuentasFuera: ReadonlySet<string>,
  destino: string | null | undefined,
): 'dentro' | 'a-fuera' | null {
  if (!m.transfer_id) {
    return null;
  }
  return !cuentasFuera.has(m.account) && !!destino && cuentasFuera.has(destino)
    ? 'a-fuera'
    : 'dentro';
}

/**
 * Regla de Vadym: no se aprueba (`reviewed = true`) un movimiento sin
 * categoría de una cuenta DENTRO del presupuesto (tampoco los ingresos: deben
 * ir a una categoría de ingreso / «Listo para asignar»). Sí se aprueban sin
 * categoría: los de cuentas fuera del presupuesto, los traspasos entre
 * cuentas y los divididos cuyas partes tengan todas categoría (o sean
 * traspasos). `sugerida` es la categoría que se pondrá al aprobar, si la hay.
 */
export function puedeAprobarse(
  m: Pick<
    MovimientoPendiente,
    'category' | 'account' | 'is_parent' | 'transfer_id' | 'partes'
  >,
  cuentasFuera: ReadonlySet<string>,
  sugerida?: string | null,
): boolean {
  if (cuentasFuera.has(m.account) || m.transfer_id) {
    return true;
  }
  if (m.is_parent) {
    const partes = m.partes ?? [];
    return (
      partes.length > 0 && partes.every(p => !!p.category || !!p.transfer_id)
    );
  }
  return !!m.category || !!sugerida;
}

/**
 * Reparte una selección entre los que se pueden aprobar y los que no. Sin
 * sugerencias: «Aprobar seleccionados» no elige categoría por nadie
 * (decisión del 6-oct-2026); el chip «¿X? ✓» de cada fila sí, porque
 * tocarlo es elegirla.
 */
export function separarAprobables(
  movimientos: readonly MovimientoPendiente[],
  ids: ReadonlySet<string>,
  cuentasFuera: ReadonlySet<string>,
): { aprobables: Set<string>; bloqueados: Set<string> } {
  const aprobables = new Set<string>();
  const bloqueados = new Set<string>();
  for (const m of movimientos) {
    if (!ids.has(m.id)) {
      continue;
    }
    if (puedeAprobarse(m, cuentasFuera)) {
      aprobables.add(m.id);
    } else {
      bloqueados.add(m.id);
    }
  }
  return { aprobables, bloqueados };
}

/**
 * Chip de un toque «¿Nómina? ✓» en Revisar: entradas de dinero sin categoría
 * ni sugerencia. Sin categoría de ingreso no cuentan para «Listo para
 * asignar» y el sueldo «desaparece» del presupuesto.
 */
export function admiteChipIngreso(
  m: MovimientoPendiente,
  cuentasFuera: ReadonlySet<string>,
  sugerida: string | null | undefined,
): boolean {
  return m.amount > 0 && !sugerida && esSinCategoria(m, cuentasFuera);
}

/** Grupos por fecha, del más reciente al más antiguo (orden estable). */
export function agruparPorFecha<T extends { date: string }>(
  movimientos: readonly T[],
): { fecha: string; movimientos: T[] }[] {
  const grupos = new Map<string, T[]>();
  for (const m of [...movimientos].sort((a, b) =>
    b.date.localeCompare(a.date),
  )) {
    const g = grupos.get(m.date);
    if (g) {
      g.push(m);
    } else {
      grupos.set(m.date, [m]);
    }
  }
  return [...grupos].map(([fecha, lista]) => ({ fecha, movimientos: lista }));
}

export function alternar(
  seleccion: ReadonlySet<string>,
  id: string,
): Set<string> {
  const s = new Set(seleccion);
  if (s.has(id)) {
    s.delete(id);
  } else {
    s.add(id);
  }
  return s;
}

/** «Seleccionar todo» / «Quitar selección». */
export function alternarTodo(
  seleccion: ReadonlySet<string>,
  ids: readonly string[],
): Set<string> {
  return todoSeleccionado(seleccion, ids) ? new Set() : new Set(ids);
}

export function todoSeleccionado(
  seleccion: ReadonlySet<string>,
  ids: readonly string[],
): boolean {
  return ids.length > 0 && ids.every(id => seleccion.has(id));
}

/** La selección sin los que ya no están pendientes (aprobados, borrados). */
export function seleccionVigente(
  seleccion: ReadonlySet<string>,
  ids: readonly string[],
): Set<string> {
  const vivos = new Set(ids);
  return new Set([...seleccion].filter(id => vivos.has(id)));
}

export function totalSeleccion(
  movimientos: readonly { id: string; amount: number }[],
  seleccion: ReadonlySet<string>,
): number {
  return movimientos.reduce(
    (s, m) => (seleccion.has(m.id) ? s + m.amount : s),
    0,
  );
}

/**
 * Categoría sugerida por beneficiario: la del movimiento categorizado más
 * reciente de ese beneficiario (`historial` en cualquier orden).
 */
export function sugerenciasPorBeneficiario(
  historial: readonly {
    payee: string | null;
    category: string | null;
    date: string;
  }[],
): Map<string, string> {
  const mejor = new Map<string, { category: string; date: string }>();
  for (const h of historial) {
    if (!h.payee || !h.category) {
      continue;
    }
    const actual = mejor.get(h.payee);
    if (!actual || h.date > actual.date) {
      mejor.set(h.payee, { category: h.category, date: h.date });
    }
  }
  return new Map([...mejor].map(([p, v]) => [p, v.category]));
}

/** La sugerencia de un pendiente (solo si no tiene categoría ni es dividido). */
export function sugerenciaDe(
  m: MovimientoPendiente,
  sugerencias: ReadonlyMap<string, string>,
): string | null {
  if (m.category || m.is_parent || !m.payee) {
    return null;
  }
  return sugerencias.get(m.payee) ?? null;
}

/**
 * Cambios para aprobar: solo `reviewed = true`; la categoría no se toca
 * (sin aplicar sugerencias, decisión del 6-oct-2026).
 */
export function cambiosAprobar(
  movimientos: readonly MovimientoPendiente[],
  ids: ReadonlySet<string>,
): { id: string; reviewed: true }[] {
  return movimientos
    .filter(m => ids.has(m.id))
    .map(m => ({ id: m.id, reviewed: true as const }));
}

/**
 * Cambios para categorizar en Revisar (H78): categorizar es corregir y
 * aprobar a la vez, en el mismo update (un solo «Deshacer» lo revierte
 * todo). Los divididos se saltan: su categoría va en las partes.
 */
export function cambiosCategorizar(
  movimientos: readonly MovimientoPendiente[],
  ids: ReadonlySet<string>,
  category: string,
): { id: string; category: string; reviewed: true }[] {
  return movimientos
    .filter(m => ids.has(m.id) && !m.is_parent)
    .map(m => ({ id: m.id, category, reviewed: true as const }));
}

/**
 * «Aprobar los N que ya tienen categoría» (H77): los que llevan categoría
 * propia (o son divididos con todas las partes categorizadas) y cumplen
 * `puedeAprobarse`. No cuenta las sugeridas: esas se aceptan una a una con
 * su chip.
 */
export function listosParaAprobar(
  movimientos: readonly MovimientoPendiente[],
  cuentasFuera: ReadonlySet<string>,
): Set<string> {
  return new Set(
    movimientos
      .filter(
        m => (!!m.category || !!m.is_parent) && puedeAprobarse(m, cuentasFuera),
      )
      .map(m => m.id),
  );
}
